import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { 
  Calendar, MapPin, Ticket, Plane, Hotel, 
  Download, ChevronRight, Music, Clock,
  TrendingUp, Loader2, Settings, Vibrate,
  CheckCircle, ArrowRight, Zap, Shield,
  Coffee, Briefcase, UtensilsCrossed, BedDouble,
  Car, LogOut
} from "lucide-react";
import { format, differenceInHours, parseISO, addHours } from "date-fns";
import { toast } from "sonner";
import Footer from "../components/Footer";
import SkeletonCard from "../components/SkeletonCard";
import SoundProfile from "../components/SoundProfile";
import { haptic, useHaptic } from "../utils/haptic";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier labels with Alchemy copy
const TIER_LABELS = {
  ticketOnly: "The Minimalist",
  ticketFlight: "The Efficiency Play",
  completeTrip: "The Psychological Moonshot"
};

// Haptic settings key
const HAPTIC_ENABLED_KEY = "xtrip_haptics_enabled";

// EARLY ARRIVAL RECOVERY Timeline Item - Dead Zone Fix for Dashboard
const EarlyArrivalRecoveryTimelineItem = ({ arrivalTime, hotelCheckinTime, hotelName }) => {
  return (
    <div className="flex gap-4 relative" data-testid="early-arrival-recovery-timeline">
      {/* Neon icon circle - Orange for recovery */}
      <div 
        className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center z-10 flex-shrink-0 animate-pulse"
        style={{ boxShadow: '0 0 16px rgba(249, 115, 22, 0.5)' }}
      >
        <BedDouble className="w-4 h-4 text-white" />
      </div>
      
      {/* Content */}
      <div className="flex-1 pb-2 p-3 rounded-xl border-2 border-orange-500/40 bg-orange-500/5">
        <div className="flex items-baseline gap-2 mb-1">
          <span 
            className="font-bold text-orange-400"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            EARLY ARRIVAL RECOVERY
          </span>
        </div>
        <p 
          className="text-xs text-muted-foreground mb-2"
          style={{ fontFamily: "'Space Mono', monospace" }}
        >
          <span className="text-orange-400">SAFE LANDING</span> • {hotelName || 'Hotel'} Lounge / Bag Drop / Breakfast
        </p>
        {/* Mini timeline */}
        <div className="flex items-center gap-2 text-[10px]" style={{ fontFamily: "'Space Mono', monospace" }}>
          <span className="text-cyan-400">{arrivalTime}</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-orange-400">RECOVERY</span>
          <span className="text-muted-foreground">→</span>
          <span className="text-purple-400">{hotelCheckinTime}</span>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [downloadingVoucher, setDownloadingVoucher] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [hapticsEnabled, setHapticsEnabled] = useState(() => {
    const stored = localStorage.getItem(HAPTIC_ENABLED_KEY);
    return stored === null ? true : stored === "true";
  });
  const { isSupported: hapticSupported } = useHaptic();

  useEffect(() => {
    fetchDashboard();
  }, []);

  // Persist haptic preference
  useEffect(() => {
    localStorage.setItem(HAPTIC_ENABLED_KEY, String(hapticsEnabled));
  }, [hapticsEnabled]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/dashboard`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        // Demo mode for unauthenticated users - show empty state
        setIsDemoMode(true);
        setDashboardData({
          user: { name: "Demo Raver", email: "demo@xtrip.com", picture: null },
          stats: { total_trips: 0, upcoming_trips: 0, cities_visited: 0, total_spent: 0 },
          upcoming_journeys: [],
          past_journeys: []
        });
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setDashboardData(data);
      setIsDemoMode(false);
    } catch (error) {
      console.error('Dashboard error:', error);
      // Fallback to empty state on error
      setIsDemoMode(true);
      setDashboardData({
        user: { name: "Demo Raver", email: "demo@xtrip.com", picture: null },
        stats: { total_trips: 0, upcoming_trips: 0, cities_visited: 0, total_spent: 0 },
        upcoming_journeys: [],
        past_journeys: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadVoucher = async (bookingId) => {
    if (hapticsEnabled) haptic("medium");
    setDownloadingVoucher(bookingId);
    try {
      const response = await fetch(`${API_URL}/api/users/bookings/${bookingId}/voucher`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate voucher');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `xtrip-vibe-voucher-${bookingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      if (hapticsEnabled) haptic("success");
      toast.success("Vibe Voucher downloaded!");
    } catch (error) {
      console.error('Voucher download error:', error);
      toast.error("Failed to download voucher");
    } finally {
      setDownloadingVoucher(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 skeleton w-1/3" />
            <div className="grid sm:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 skeleton rounded-xl" />
              ))}
            </div>
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <SkeletonCard key={i} variant="feed" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { user, stats, upcoming_journeys, past_journeys } = dashboardData;

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Demo Mode Banner */}
        {isDemoMode && (
          <div 
            className="mb-6 p-4 rounded-xl border-2 border-cyan-400/40 bg-cyan-400/5"
            style={{ boxShadow: '0 0 20px rgba(34, 211, 238, 0.15)' }}
            data-testid="demo-mode-banner"
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-400" />
                <span 
                  className="text-cyan-400 font-bold uppercase tracking-wider text-sm"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  Demo Mode
                </span>
              </div>
              <span className="text-muted-foreground text-sm">
                Sign in to see your Raver-Roadmap with real bookings.
              </span>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-16 h-16 rounded-full border-2 border-primary"
              />
            )}
            <div>
              <h1 
                className="text-2xl sm:text-3xl font-bold text-foreground"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                {isDemoMode ? "Raver-Roadmap" : `Welcome back, ${user?.name?.split(' ')[0]}`}
              </h1>
              <p 
                className="text-muted-foreground text-sm"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                {isDemoMode ? "Your journey timeline • All logistics handled" : user?.email}
              </p>
            </div>
          </div>
          
          {/* Settings Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
            className="text-muted-foreground hover:text-foreground"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="glass rounded-xl p-4 mb-8 animate-fade-in-up" style={{ animationFillMode: 'forwards' }}>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-4">
              Settings
            </h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Vibrate className="w-5 h-5 text-secondary" />
                <div>
                  <p className="text-sm font-medium text-foreground">Haptic Feedback</p>
                  <p className="text-xs text-muted-foreground">
                    {hapticSupported ? "Vibration on interactions" : "Not supported on this device"}
                  </p>
                </div>
              </div>
              <Switch
                checked={hapticsEnabled}
                onCheckedChange={setHapticsEnabled}
                disabled={!hapticSupported}
              />
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {[
            { label: "Total Trips", value: stats?.total_trips || 0, icon: Ticket, color: "text-primary" },
            { label: "Upcoming", value: stats?.upcoming_trips || 0, icon: Calendar, color: "text-accent" },
            { label: "Cities", value: stats?.cities_visited || 0, icon: MapPin, color: "text-secondary" },
            { label: "Total Spent", value: `£${Math.round(stats?.total_spent || 0)}`, icon: TrendingUp, color: "text-primary" }
          ].map((stat) => (
            <div key={stat.label} className="glass rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon className={`w-4 h-4 ${stat.color}`} />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">
                  {stat.label}
                </span>
              </div>
              <div className={`font-mono text-2xl font-bold ${stat.color}`}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Two Column Layout: Roadmap + Sound Profile */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          {/* Your Upcoming Raver-Roadmap - Takes 2/3 */}
          <section className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground mb-2 flex items-center gap-2">
              <Zap className="w-5 h-5 text-accent" />
              Your Upcoming Raver-Roadmap
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Every moment accounted for. Zero friction.
            </p>
            
            {upcoming_journeys?.length > 0 ? (
              <div className="space-y-6">
                {upcoming_journeys.map((booking) => (
                  <RaverRoadmapCard 
                    key={booking.booking_id}
                    booking={booking}
                    onDownloadVoucher={handleDownloadVoucher}
                    downloadingVoucher={downloadingVoucher}
                    hapticsEnabled={hapticsEnabled}
                  />
                ))}
              </div>
            ) : (
              <div className="glass rounded-xl p-8 text-center">
                <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">No upcoming journeys yet</p>
                <Button onClick={() => navigate('/events')} className="bg-primary">
                  Find Your Next Experience
                </Button>
              </div>
            )}
          </section>

          {/* Sound Profile - Takes 1/3 */}
          <aside className="lg:col-span-1">
            <SoundProfile />
          </aside>
        </div>

        {/* Past Journeys */}
        {past_journeys?.length > 0 && (
          <section className="mb-12">
            <h2 className="font-heading text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-muted-foreground" />
              Past Journeys
            </h2>
            
            <div className="space-y-4">
              {past_journeys.map((booking) => (
                <PastJourneyCard 
                  key={booking.booking_id}
                  booking={booking}
                  onDownloadVoucher={handleDownloadVoucher}
                  downloadingVoucher={downloadingVoucher}
                />
              ))}
            </div>
          </section>
        )}
      </div>
      <Footer />
    </div>
  );
};

// Helper: Parse datetime safely
const safeParseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    if (typeof dateStr === 'string') {
      return parseISO(dateStr.replace('Z', '+00:00'));
    }
    return new Date(dateStr);
  } catch {
    return null;
  }
};

// Raver Roadmap Timeline Card - REAL DATA VERSION
const RaverRoadmapCard = ({ booking, onDownloadVoucher, downloadingVoucher, hapticsEnabled }) => {
  const event = booking.event || {};
  const flight = booking.flight || {};
  const hotel = booking.hotel || {};
  const tier = booking.tier || "ticketOnly";
  const tierLabel = TIER_LABELS[tier] || "Experience";
  
  // Parse event dates
  const eventDate = safeParseDate(event.start_at || event.date);
  const eventEndDate = safeParseDate(event.end_at);
  
  // Parse flight data from bundle
  const outbound = flight.outbound || {};
  const returnFlight = flight.return || {};
  
  // Calculate timeline milestones from real data
  const outFlightDeparture = safeParseDate(outbound.departure_time || outbound.departure);
  const outFlightArrival = safeParseDate(outbound.arrival_time || outbound.arrival);
  
  const retFlightDeparture = safeParseDate(returnFlight.departure_time || returnFlight.departure);
  const retFlightArrival = safeParseDate(returnFlight.arrival_time || returnFlight.arrival);
  
  // Origin and destination from booking or flight data
  const originCity = booking.origin || outbound.origin || "Origin";
  const destCity = booking.destination || outbound.destination || event.city || "Destination";
  
  // Detect early arrival (flight arrives between 00:00-08:00)
  const isEarlyArrival = outFlightArrival && outFlightArrival.getHours() < 8;
  
  // Hotel check-in time (early if early arrival)
  let hotelCheckinTime = null;
  if (tier === 'completeTrip' && eventDate) {
    if (isEarlyArrival) {
      // Early check-in at 06:00
      hotelCheckinTime = new Date(eventDate);
      hotelCheckinTime.setHours(6, 0, 0, 0);
    } else {
      // Standard check-in at 15:00 (3 PM)
      hotelCheckinTime = new Date(eventDate);
      hotelCheckinTime.setHours(15, 0, 0, 0);
    }
  }
  
  // Recovery / Late checkout time (next day 14:00)
  const recoveryCheckout = eventDate ? addHours(eventDate, 38) : null; // ~14:00 next day
  
  const now = new Date();
  const hoursUntilEvent = eventDate ? differenceInHours(eventDate, now) : 0;
  
  // Hotel name from real data
  const hotelName = hotel.name || "Hotel";
  
  // Flight numbers/carrier
  const carrierName = flight.carrier || "Airline";
  
  return (
    <div className="glass rounded-2xl overflow-hidden border border-accent/20" data-testid="raver-roadmap-card">
      {/* Header */}
      <div className="relative">
        <div className="h-32 sm:h-40 relative">
          <img 
            src={event.image_url || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800"}
            alt={event.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          
          {/* Time Badge */}
          {hoursUntilEvent > 0 && (
            <div className="absolute top-4 right-4">
              <div 
                className="px-3 py-1.5 rounded-full bg-accent/90 text-accent-foreground text-sm flex items-center gap-1.5"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                <Clock className="w-3.5 h-3.5" />
                {hoursUntilEvent < 48 ? `${hoursUntilEvent}h` : `${Math.floor(hoursUntilEvent / 24)}d`}
              </div>
            </div>
          )}
          
          {/* Tier Badge */}
          <div className="absolute top-4 left-4">
            <span 
              className="px-2 py-1 bg-secondary/80 text-secondary-foreground text-xs rounded"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              {tierLabel}
            </span>
          </div>
        </div>
        
        {/* Event Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 
            className="font-bold text-xl text-foreground mb-1"
            style={{ fontFamily: "'Space Mono', monospace" }}
            data-testid="event-name"
          >
            {event.name || "Event"}
          </h3>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-secondary" />
              {event.city}
            </span>
            {eventDate && (
              <span 
                className="flex items-center gap-1 text-xs"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                <Calendar className="w-3.5 h-3.5 text-secondary" />
                {format(eventDate, 'MMM dd')}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* RAVER-ROADMAP Timeline - Vertical Layout with Space Mono */}
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <span 
            className="text-xs text-muted-foreground uppercase tracking-wider"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            Raver-Roadmap
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>
        
        <div className="relative" data-testid="raver-roadmap-timeline">
          {/* Timeline Line - Neon gradient */}
          <div 
            className="absolute left-4 top-6 bottom-6 w-0.5"
            style={{ 
              background: 'linear-gradient(to bottom, #22d3ee, #a855f7, #ccff00, #f97316, #22d3ee)'
            }}
          />
          
          {/* Timeline Items - THE RAVER-MANIFEST: 7 POINTS */}
          <div className="space-y-4">
            {/* 1. DEPARTURE FLIGHT: [Origin] -> [Event City] | [Time] */}
            {tier !== 'ticketOnly' && outFlightDeparture && (
              <TimelineItem 
                icon={Plane}
                color="cyan"
                title="Departure Flight"
                time={format(outFlightDeparture, 'HH:mm')}
                date={format(outFlightDeparture, 'MMM dd')}
                description={`${originCity} → ${destCity} | ${format(outFlightDeparture, 'HH:mm')}${outFlightArrival ? ` - ${format(outFlightArrival, 'HH:mm')}` : ''}`}
                prefix={carrierName}
                testId="departure-flight"
              />
            )}
            
            {/* EARLY ARRIVAL RECOVERY - Dead Zone Fix */}
            {tier !== 'ticketOnly' && isEarlyArrival && outFlightArrival && (
              <EarlyArrivalRecoveryTimelineItem 
                arrivalTime={format(outFlightArrival, 'HH:mm')}
                hotelCheckinTime={hotelCheckinTime ? format(hotelCheckinTime, 'HH:mm') : '06:00'}
                hotelName={hotelName}
              />
            )}
            
            {/* 2. HOTEL CHECK-IN: [Actual Hotel Name] | [Time] */}
            {tier === 'completeTrip' && hotelCheckinTime && (
              <TimelineItem 
                icon={Hotel}
                color="purple"
                title="Hotel Check-in"
                time={format(hotelCheckinTime, 'HH:mm')}
                date={format(hotelCheckinTime, 'MMM dd')}
                description={`${hotelName} | ${format(hotelCheckinTime, 'HH:mm')}`}
                testId="hotel-checkin"
              />
            )}
            
            {/* 3. EVENT TIMING: [Entry Time] to [Closing Time] */}
            {eventDate && (
              <TimelineItem 
                icon={Music}
                color="primary"
                title="Event Timing"
                time={format(eventDate, 'HH:mm')}
                date={format(eventDate, 'MMM dd')}
                description={`${event.venue_name || 'Venue'} | ${format(eventDate, 'HH:mm')}${eventEndDate ? ` to ${format(eventEndDate, 'HH:mm')}` : ''}`}
                testId="event-timing"
              />
            )}
            
            {/* 4. HOTEL CHECK-OUT: [Actual Hotel Name] | [Time] */}
            {tier === 'completeTrip' && recoveryCheckout && (
              <TimelineItem 
                icon={LogOut}
                color="orange"
                title="Hotel Check-out"
                time={format(recoveryCheckout, 'HH:mm')}
                date={format(recoveryCheckout, 'MMM dd')}
                description={`${hotelName} | ${format(recoveryCheckout, 'HH:mm')} (Late Checkout)`}
                testId="hotel-checkout"
              />
            )}
            
            {/* 5. TRANSFER: [Distance/Time] to Departure Airport */}
            {tier !== 'ticketOnly' && retFlightDeparture && (
              <TimelineItem 
                icon={Car}
                color="purple"
                title="Transfer"
                time={format(addHours(retFlightDeparture, -2), 'HH:mm')}
                date={format(retFlightDeparture, 'MMM dd')}
                description={`~45min to ${destCity} Airport | Depart Hotel ${format(addHours(retFlightDeparture, -2), 'HH:mm')}`}
                testId="transfer"
              />
            )}
            
            {/* 6. RETURN FLIGHT: [Event City] -> [Origin City] | [Time] */}
            {tier !== 'ticketOnly' && retFlightDeparture && (
              <TimelineItem 
                icon={Plane}
                color="cyan"
                title="Return Flight"
                time={format(retFlightDeparture, 'HH:mm')}
                date={format(retFlightDeparture, 'MMM dd')}
                description={`${destCity} → ${originCity} | ${format(retFlightDeparture, 'HH:mm')}${retFlightArrival ? ` - ${format(retFlightArrival, 'HH:mm')}` : ''}`}
                prefix={carrierName}
                testId="return-flight"
              />
            )}
            
            {/* 7. ARRIVAL HOME: [Origin Airport] | [Time] */}
            {tier !== 'ticketOnly' && retFlightArrival && (
              <TimelineItem 
                icon={MapPin}
                color="cyan"
                title="Arrival Home"
                time={format(retFlightArrival, 'HH:mm')}
                date={format(retFlightArrival, 'MMM dd')}
                description={`${originCity} Airport | ${format(retFlightArrival, 'HH:mm')} - Trip Complete`}
                isLast
                testId="arrival-home"
              />
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-4 border-t border-border/30">
          <Button
            onClick={() => onDownloadVoucher(booking.booking_id)}
            disabled={downloadingVoucher === booking.booking_id}
            className="flex-1 bg-gradient-to-r from-secondary to-accent text-white font-bold"
            style={{ fontFamily: "'Space Mono', monospace" }}
            data-testid="download-voucher-btn"
          >
            {downloadingVoucher === booking.booking_id ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Vibe Voucher
          </Button>
          <Link to={`/events/${booking.event_id}`} className="flex-1">
            <Button 
              variant="outline" 
              className="w-full border-border"
              style={{ fontFamily: "'Space Mono', monospace" }}
              data-testid="view-event-btn"
            >
              View Event
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        
        {/* Vibe Track */}
        {booking.vibe_track && (
          <div className="mt-4 p-3 rounded-lg bg-secondary/10 border border-secondary/20">
            <div className="flex items-center gap-2 text-xs">
              <Music className="w-3.5 h-3.5 text-secondary" />
              <span className="text-muted-foreground">Booked while vibing to:</span>
              <span className="text-secondary" style={{ fontFamily: "'Space Mono', monospace" }}>{booking.vibe_track}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Timeline Item Component - Space Mono + Neon accents + Glowing borders
const TimelineItem = ({ icon: Icon, color, title, time, date, description, prefix, isLast, testId }) => {
  // Neon color classes with glowing border styling
  const colorClasses = {
    primary: { bg: "bg-primary", text: "text-primary", glow: "rgba(204, 255, 0, 0.4)", border: "border-primary/40" },
    cyan: { bg: "bg-cyan-400", text: "text-cyan-400", glow: "rgba(34, 211, 238, 0.4)", border: "border-cyan-400/40" },
    purple: { bg: "bg-purple-400", text: "text-purple-400", glow: "rgba(168, 85, 247, 0.4)", border: "border-purple-400/40" },
    orange: { bg: "bg-orange-400", text: "text-orange-400", glow: "rgba(251, 146, 60, 0.4)", border: "border-orange-400/40" }
  };
  
  const colorStyle = colorClasses[color] || colorClasses.primary;
  
  return (
    <div className="flex gap-4 relative" data-testid={testId}>
      {/* Neon icon circle */}
      <div 
        className={`w-8 h-8 rounded-full ${colorStyle.bg} flex items-center justify-center z-10 flex-shrink-0`}
        style={{ boxShadow: `0 0 16px ${colorStyle.glow}` }}
      >
        <Icon className="w-4 h-4 text-background" />
      </div>
      
      {/* Content - with neon border styling for technical data */}
      <div 
        className={`flex-1 pb-2 p-2 rounded-lg border ${colorStyle.border} bg-background/50`}
        style={{ boxShadow: `0 0 8px ${colorStyle.glow}` }}
      >
        <div className="flex items-baseline gap-2 mb-1">
          <span 
            className={`font-bold ${colorStyle.text}`}
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            {title}
          </span>
          <span 
            className="text-xs text-muted-foreground"
            style={{ fontFamily: "'Space Mono', monospace" }}
          >
            {time}
          </span>
        </div>
        <p 
          className="text-xs text-muted-foreground"
          style={{ fontFamily: "'Space Mono', monospace" }}
        >
          {prefix && <span className={colorStyle.text}>{prefix} | </span>}
          {description}
        </p>
      </div>
    </div>
  );
};

// Past Journey Card (simpler version)
const PastJourneyCard = ({ booking, onDownloadVoucher, downloadingVoucher }) => {
  const event = booking.event || {};
  const eventDate = safeParseDate(event.date || event.start_at);
  const tierLabel = TIER_LABELS[booking.tier] || "Experience";
  
  return (
    <div className="glass rounded-xl p-4 opacity-70 hover:opacity-100 transition-opacity" data-testid="past-journey-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src={event.image_url || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=200"}
              alt={event.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <h3 className="font-medium text-foreground">{event.name || "Event"}</h3>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span>{event.city}</span>
              {eventDate && (
                <>
                  <span>•</span>
                  <span style={{ fontFamily: "'Space Mono', monospace" }}>{format(eventDate, 'MMM dd, yyyy')}</span>
                </>
              )}
            </div>
            <span className="text-xs text-secondary" style={{ fontFamily: "'Space Mono', monospace" }}>{tierLabel}</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary" style={{ fontFamily: "'Space Mono', monospace" }}>
            £{booking.total_amount?.toFixed(2)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDownloadVoucher(booking.booking_id)}
            disabled={downloadingVoucher === booking.booking_id}
            data-testid="past-download-voucher-btn"
          >
            {downloadingVoucher === booking.booking_id ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
